import type { BackupsApiResponse, TasksApiResponse, TaskDetailApiResponse, AuditLogsApiResponse, BackupSchedulesApiResponse, BackupDownloadResponse } from '@/types/project';
import { apiRequest } from '@/lib/api';

export async function getBackups(projectId: string, page: number = 1): Promise<BackupsApiResponse> {
  return apiRequest(`/api/project/${projectId}/backups?page=${page}`);
}

export async function createBackup(
  projectId: string, 
  deploymentId: number, 
  comment?: string
): Promise<{ success: boolean; backup_id?: number; message?: string; error?: string }> {
  return apiRequest(`/api/project/${projectId}/backups`, {
    method: 'POST',
    body: JSON.stringify({ deployment_id: deploymentId, comment }),
  });
}

export async function deleteBackup(
  projectId: string, 
  backupId: number
): Promise<{ success: boolean; message?: string; error?: string }> {
  return apiRequest(`/api/project/${projectId}/backups/${backupId}`, {
    method: 'DELETE',
  });
}

export async function downloadBackup(
  projectId: string, 
  backupId: number
): Promise<BackupDownloadResponse> {
  return apiRequest(`/api/project/${projectId}/backups/${backupId}/download`);
}

export async function restoreBackup(
  projectId: string, 
  backupId: number, 
  deploymentId: number
): Promise<{ success: boolean; message?: string; error?: string }> {
  return apiRequest(`/api/project/${projectId}/backups/${backupId}/restore`, {
    method: 'POST',
    body: JSON.stringify({ deployment_id: deploymentId }),
  });
}

// Backup Upload API
export interface StartMultipartUploadResponse {
  success: boolean;
  backup_id?: number;
  task_id?: string;
  upload_id?: string;
  bucket?: string;
  key?: string;
  error?: string;
}

export interface PartUploadUrlResponse {
  success: boolean;
  url?: string;
  error?: string;
}

export interface CompleteMultipartUploadResponse {
  success: boolean;
  backup_id?: number;
  error?: string;
}

export async function startMultipartUpload(
  projectId: string,
  comment?: string
): Promise<StartMultipartUploadResponse> {
  return apiRequest(`/api/project/${projectId}/backups/multipart/start`, {
    method: 'POST',
    body: JSON.stringify({ comment }),
  });
}

export async function getPartUploadUrl(
  projectId: string,
  uploadId: string,
  partNumber: number,
  bucket: string,
  key: string
): Promise<PartUploadUrlResponse> {
  return apiRequest(`/api/project/${projectId}/backups/multipart/part-url`, {
    method: 'POST',
    body: JSON.stringify({
      upload_id: uploadId,
      part_number: partNumber,
      bucket,
      key,
    }),
  });
}

export async function completeMultipartUpload(
  projectId: string,
  uploadId: string,
  parts: Array<{ ETag: string; PartNumber: number }>,
  bucket: string,
  key: string,
  backupId: number,
  size: number
): Promise<CompleteMultipartUploadResponse> {
  return apiRequest(`/api/project/${projectId}/backups/multipart/complete`, {
    method: 'POST',
    body: JSON.stringify({
      upload_id: uploadId,
      parts,
      bucket,
      key,
      backup_id: backupId,
      size,
    }),
  });
}

// Storage Validation API
export interface StorageValidationResponse {
  success?: boolean;
  error?: string;
  server_id?: number;
  server_name?: string;
  backup_size_mb?: number;
  backup_size_gb?: number;
  required_space_gb?: number;
  available_space_gb?: number;
  has_sufficient_space?: boolean;
  space_shortage_gb?: number;
}

export async function validateServerStorage(
  serverId: number,
  backupSizeMb: number
): Promise<StorageValidationResponse> {
  return apiRequest(`/api/v1/servers/${serverId}/storage/validate`, {
    method: 'POST',
    body: JSON.stringify({ backup_size_mb: backupSizeMb }),
  });
}

// Backup Scheduler APIs
export async function getBackupSchedules(projectId: string): Promise<BackupSchedulesApiResponse> {
  return apiRequest(`/api/project/${projectId}/backup-schedules`);
}

export async function createBackupSchedule(
  projectId: string,
  data: {
    name: string;
    build_id: number;
    frequency: string;
    retention_count: number;
    comments?: string;
    hours_interval?: number;
    minute?: number;
    daily_time?: string;
    weekly_time?: string;
    day_of_week?: number;
    monthly_time?: string;
    day_of_month?: number;
    cron_expression?: string;
  }
): Promise<{ success: boolean; schedule_id?: number; message?: string; error?: string }> {
  return apiRequest(`/api/project/${projectId}/backup-schedules`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function toggleBackupSchedule(
  projectId: string,
  scheduleId: number,
  active: boolean
): Promise<{ success: boolean; active?: boolean; error?: string }> {
  return apiRequest(`/api/project/${projectId}/backup-schedules/${scheduleId}/toggle`, {
    method: 'POST',
    body: JSON.stringify({ active }),
  });
}

export async function runBackupScheduleNow(
  projectId: string,
  scheduleId: number
): Promise<{ success: boolean; message?: string; run_id?: number; error?: string }> {
  return apiRequest(`/api/project/${projectId}/backup-schedules/${scheduleId}/run-now`, {
    method: 'POST',
  });
}

export async function deleteBackupSchedule(
  projectId: string,
  scheduleId: number,
  deleteBackups: boolean = false
): Promise<{ success: boolean; message?: string; error?: string }> {
  const url = `/api/project/${projectId}/backup-schedules/${scheduleId}${deleteBackups ? '?delete_backups=true' : ''}`;
  return apiRequest(url, {
    method: 'DELETE',
  });
}

export async function getBackupSchedule(
  projectId: string,
  scheduleId: number
): Promise<{ 
  id: number;
  name: string;
  build_id: number;
  frequency: string;
  day_of_week: number | null;
  day_of_month: number | null;
  hour: number | null;
  minute: number | null;
  hours_interval: number | null;
  cron_expression: string | null;
  retention_count: number;
  active: boolean;
  next_run: string | null;
  last_run: string | null;
  comments: string;
}> {
  return apiRequest(`/api/project/${projectId}/backup-schedules/${scheduleId}`);
}

export async function editBackupSchedule(
  projectId: string,
  scheduleId: number,
  data: {
    name: string;
    frequency: string;
    retention_count: number;
    comments?: string;
    hours_interval?: number;
    minute?: number;
    daily_time?: string;
    weekly_time?: string;
    day_of_week?: number;
    monthly_time?: string;
    day_of_month?: number;
    cron_expression?: string;
  }
): Promise<{ success: boolean; message?: string; error?: string }> {
  const formData = new FormData();
  formData.append('name', data.name);
  formData.append('frequency', data.frequency);
  formData.append('retention_count', data.retention_count.toString());
  if (data.comments) formData.append('comments', data.comments);
  if (data.hours_interval !== undefined) formData.append('hours_interval', data.hours_interval.toString());
  if (data.minute !== undefined) formData.append('minute', data.minute.toString());
  if (data.daily_time) formData.append('daily_time', data.daily_time);
  if (data.weekly_time) formData.append('weekly_time', data.weekly_time);
  if (data.day_of_week !== undefined) formData.append('day_of_week', data.day_of_week.toString());
  if (data.monthly_time) formData.append('monthly_time', data.monthly_time);
  if (data.day_of_month !== undefined) formData.append('day_of_month', data.day_of_month.toString());
  if (data.cron_expression) formData.append('cron_expression', data.cron_expression);

  const response = await fetch(`/api/project/${projectId}/backup-schedules/${scheduleId}/edit`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  
  if (!response.ok) {
    return { success: false, error: 'Failed to update schedule' };
  }
  return { success: true };
}

export async function getTasks(
  projectId: string, 
  page: number = 1,
  filters?: { status?: string; type?: string }
): Promise<TasksApiResponse> {
  const params = new URLSearchParams({ page: page.toString() });
  if (filters?.status) params.set('status', filters.status);
  if (filters?.type) params.set('type', filters.type);
  
  return apiRequest(`/api/project/${projectId}/tasks?${params}`);
}

export async function getTask(projectId: string, taskId: number): Promise<TaskDetailApiResponse> {
  return apiRequest(`/api/project/${projectId}/tasks/${taskId}`);
}

export async function cancelTask(projectId: string, taskId: number): Promise<{ success: boolean; message?: string; error?: string }> {
  return apiRequest(`/api/project/${projectId}/tasks/${taskId}/cancel`, {
    method: 'POST',
  });
}

export async function getAuditLogs(projectId: string, page: number = 1): Promise<AuditLogsApiResponse> {
  return apiRequest(`/api/project/${projectId}/audit-logs?page=${page}`);
}

export interface AIConversation {
  id: string;
  project_id: string;
  user_id: number | null;
  build_id: number;
  title: string | null;
  message_count: number;
  last_message_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AIConversationsResponse {
  conversations: AIConversation[];
  total: number;
  limit: number;
  offset: number;
}

export async function getAIConversations(
  projectId: string,
  limit: number = 50,
  offset: number = 0
): Promise<AIConversationsResponse> {
  return apiRequest(`/api/ai/agent/projects/${projectId}/conversations?limit=${limit}&offset=${offset}`);
}

export async function deleteAIConversation(conversationId: string): Promise<void> {
  await apiRequest(`/api/ai/agent/conversations/${conversationId}`, {
    method: 'DELETE',
  });
}

export interface AIMessage {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  tool_name: string | null;
  tool_result: string | null;
  tokens_used: number | null;
  created_at: string;
}

export interface AIMessagesResponse {
  messages: AIMessage[];
}

export async function getAIConversationMessages(
  conversationId: string,
  limit: number = 100
): Promise<AIMessagesResponse> {
  return apiRequest(`/api/ai/agent/conversations/${conversationId}/messages?limit=${limit}`);
}

export async function updateAIConversationTitle(
  conversationId: string,
  title: string
): Promise<void> {
  await apiRequest(`/api/ai/agent/conversations/${conversationId}/title`, {
    method: 'PATCH',
    body: JSON.stringify({ title }),
  });
}
